import { useQueryClient } from 'react-query';
import QUERY_CACHE_KEY from '@constants/QueryCacheKey';
import FeedDetailedCached from '@src/utils/feed-detailed-cache';
import useApiMutation from './useApiMutation';

function useUnreactToPostMutation(postId, options) {
  const queryClient = useQueryClient();
  const queryKey = QUERY_CACHE_KEY.post(postId);

  return useApiMutation('delete', `/v1/posts/${postId}/reactions`, {
    ...options,
    onMutate: (...args) => {
      const feedCached = FeedDetailedCached.getFeedDetailedCached();

      let previousData = null;
      feedCached.forEach(([, queryData]) => {
        if (queryData?.pages) {
          queryData.pages.forEach((page) => {
            const foundItem = page.items.find((item) => {
              return item.type === 'post' && item.value.id === postId;
            });

            if (foundItem) {
              previousData = foundItem.value;
            }
          });
        }
      });

      // Calculate updated data once using previousData
      let updatedPostData = null;
      if (previousData) {
        const currentReaction = previousData.currentUserReactionType;
        let newLikesCount = previousData.likesCount || 0;
        let newHeartsCount = previousData.heartsCount || 0;

        // Remove the appropriate count based on current reaction
        if (currentReaction === 'like') {
          newLikesCount = Math.max(0, newLikesCount - 1);
        } else if (currentReaction === 'heart') {
          newHeartsCount = Math.max(0, newHeartsCount - 1);
        }

        updatedPostData = {
          ...previousData,
          currentUserReactionType: null,
          currentUserLikes: false,
          likesCount: newLikesCount,
          heartsCount: newHeartsCount,
        };
      }

      // Update feed cache using the calculated data
      if (updatedPostData) {
        feedCached.forEach(([feedQueryKey, queryData]) => {
          if (queryData?.pages) {
            const updatedPages = queryData.pages.map((page) => {
              const updatedItems = page.items.map((item) => {
                if (item.type === 'post' && item.value.id === postId) {
                  return {
                    ...item,
                    value: updatedPostData,
                  };
                }
                return item;
              });

              return {
                ...page,
                items: updatedItems,
              };
            });

            queryClient.setQueryData(feedQueryKey, {
              ...queryData,
              pages: updatedPages,
            });
          }
        });

        // Update the specific post query
        queryClient.setQueryData(queryKey, updatedPostData);
      }

      if ('function' === typeof options?.onMutate) {
        options.onMutate(...args);
      }

      return { previousData };
    },
    onError: (err, data, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
        FeedDetailedCached.updateFeedDetailedCached('post', context.previousData);
      }

      if ('function' === typeof options?.onError) {
        options.onError(err, data, context);
      }
    },
    onSuccess: (...args) => {
      queryClient.invalidateQueries(queryKey);

      if ('function' === typeof options?.onSuccess) {
        options.onSuccess(...args);
      }
    },
  });
}

export default useUnreactToPostMutation;
